import React, {useEffect, useRef, useState} from "react";
import {observer} from "mobx-react-lite";
import {categoryStore} from "../../../../Stores/CategoryStore";
import css from "../CreatePost.module.css";
import {
    Box,
    Button, Grid,
} from "@mui/material";
import {MobileContainer, SizedBox} from "../../../Global/Global";
import DropDown from "../../../Form/DropDown";
import {useForm} from "react-hook-form";
import InputText from "../../../Form/InputText";
import InputTextArea from "../../../Form/InputTextArea";
import InputZip from "../../../Form/InputZip";
import {zipToCity} from "../../../../Constants/Constants";
import InputPrice from "../../../Form/InputPrice";
import PostService from "../../../../Services/PostsService";
import InputFiles from "../../../Form/InputFiles";
import {FaTrash, FaCheck} from "react-icons/fa";
import Compressor from "compressorjs";
import {compressAccurately} from "image-conversion";
import {accountStore} from "../../../../Stores/AccountStore";
import {useLocation, useParams} from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";
import useSnackbar from "../../../Snackbar/SnackbarHook";
import accountService from "../../../../Services/AccountService";
import Space from "../../../Common/Space";
import {useMediaContext} from "../../../../MediaContext";
import {reportException} from "../../../../Utilities/sentry";
import {useNavigate} from "react-router-dom";
import {Section} from "../Section";
import {PostDetailSkeleton, SkeletonZipField} from "./Skeletons";
import {Tiers} from "./Tiers/Tiers";
import CustomFilters from "./CustomFilters";

const conditions = {
    Perfekt: "Perfect",
    God: "Good",
    Okay: "Decent",
    Defekt: "Defect",
};

const TITLE_MAX_LENGTH = 45;
const BODY_MAX_LENGTH = 1000;

function PostDetails() {
    const param = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const snackbar = useSnackbar();
    const mediaContext = useMediaContext();
    const isMobileSize = mediaContext.isMobileSize;
    const infoSectionRef = useRef(null);

    const formMethods = useForm();
    const { register, setValue, formState: { errors }, handleSubmit, unregister } = formMethods;

    const [category, setCategory] = useState();
    const [images, setImages] = useState([]); // Can contain both URLs and file objects
    const [isUploading, setIsUploading] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const [zip, setZipCode] = useState("");
    const [remotePost, setRemotePost] = useState();

    const path = location.pathname; // e.g. "/rediger-annonce/1"
    const isRemote = path.split("/")[1] == "rediger-annonce";

    const fetchZipCode = async () => {
        const zipCode = await accountService.getUserZipCode()
        setZipCode(zipCode ?? "");
    }

    const initializeNew = async () => {
        const categoryId = param.categoryId;
        const category = categoryStore.categoryList.find(category => category.id == categoryId);

        if (category == null) {
            snackbar.handleOpen("Kategorien kunne ikke findes", "error");
            return navigate("/opret-annonce");
        }

        if (category.requiresMitid && !accountStore.user.mitIdValidated) {
            snackbar.handleOpen("Kategorien kræver MitID validering", "error");
            return navigate("/opret-annonce");
        }

        await fetchZipCode();

        setCategory(category)
        setIsInitialized(true);
    }

    const initializeRemote = async () => {
        const remotePost = await PostService.getPostForEdit(param.postId);

        if (remotePost == null) {
            snackbar.handleOpen("Annoncen kunne ikke findes", "error");
            return navigate("/opret-annonce");
        }

        const category = categoryStore.categoryList.find(category => category.id == remotePost.CategoryId);

        if (category == null) {
            snackbar.handleOpen("Kategorien kunne ikke findes", "error");
            return navigate("/opret-annonce");
        }

        const urls = remotePost.Images.map((img) => {
            return img.Url;
        });

        setRemotePost(remotePost);
        setImages(urls);
        setCategory(category)
        setIsInitialized(true);
    }

    const initialize = async () => {
        if (isRemote) {
            await initializeRemote();
        } else {
            await initializeNew();
        }
    };

    useEffect(() => {
        if (isInitialized || !categoryStore.isInitialized || !accountStore.isInitialized)
            return;

        initialize();
    }, [param.postId, categoryStore.isInitialized, accountStore.isInitialized, isInitialized]);

    const uploadImagesToS3 = async (images) => {
        let s3links = [];

        /*
              Upload the images to AWS S3
              Old images are re-uploaded to get new Urls
              This is because all old URLs are deleted from AWS upon updates in the backend
            */
        for (const [index, image] of images.entries()) {
            let blob = image; // Image to upload

            // If old image
            if (typeof image === "string") {
                // Download image
                const response = await fetch(image, { cache: 'no-store' });
                blob = await response.blob();
            }

            // Get secure URL for S3 upload
            const uploadLink = await PostService.getSignedS3Url();

            // Upload image to S3
            const downloadLink = await PostService.postS3Image(uploadLink, blob);

            s3links.push({
                url: downloadLink,
                order: index + 1,
            });
        }

        return s3links;
    };

    const onSubmit = async (event) => {
        const post = {};
        let s3links = [];
        const errorMessage = "Indrykningen af annoncen fejlede";

        // If the category requires MitID the user has to be validated
        if (category.requiresMitid && !accountStore.user.mitIdValidated) {
            snackbar.handleOpen("Mangler MitID validering", "error");
            return setIsUploading(false);
        }

        setIsUploading(true);

        try {
            s3links = await uploadImagesToS3(images);
        } catch (e) {
            setIsUploading(false);
            reportException(e, {notify: true});
            return snackbar.handleOpen(errorMessage, "error");
        }

        // Transfer information to post object
        post.heading = event.title;
        post.price = parseInt(event.price.replace(/\./g, ""));
        post.body = event.description;
        post.zip = event.zipcode;
        post.condition = event.condition;
        post.images = s3links;
        post.tierId = event.tier;
        post.categoryId = category.id;
        post.desiredDaysActive = event.days;

        post.customFilters = Object.keys(event)
            .filter(key => key.startsWith('customFilter_'))
            .filter(key => event[key] !== "" && event[key] != null)
            .map(key => ({
                Key: key.replace('customFilter_', ''),
                Value: event[key]
            }));

        // The user id will be retrieved from the token in the backend
        post.userId = "";

        try {
            let response;

            if (isRemote) {
                post.postId = remotePost.PostId;
                response = await PostService.updatePost(post, event.payment);
            }

            if (!isRemote) {
                response = await PostService.createPost(post, event.payment);
            }

            const url = new URL(response.url);
            const redirectLocally = url.hostname.includes("jagtbasen.dk") || url.hostname.includes("localhost");

            if (redirectLocally) {
                navigate(url.pathname + url.search);
            }

            if (!redirectLocally) {
                window.location.href = url.href;  // external domain
            }
        } catch (e) {
            console.log(e);
            reportException(e, {notify: true});
            snackbar.handleOpen(errorMessage, "error");
        }

        setIsUploading(false);
    };

    // Return null if an exception happened
    const processImage = async (image) => {
        try {
            image = await compressAndConvert(image);

            return image;
        } catch (e) {
            reportException(e);
            snackbar.handleOpen("Fejl med billede", "error");
        }
    };

    const compressAndConvert = async (image) => {
        // Scale the image to a max width/height of 1000 pixel
        image = await new Promise((resolve, reject) => {
            // Try catch is only there for SonarLint
            try {
                new Compressor(image, {
                    quality: 1,
                    maxHeight: 1000,
                    maxWidth: 1000,
                    convertSize: 0,
                    convertTypes: "image/*",
                    success: resolve,
                    error: reject,
                });
            } catch (e) {
                reportException(e);
            }
        });

        // Make sure the image in no bigger than 300 KB
        return await compressAccurately(image, {
            size: 300,
            accuracy: 0.95,
            type: "image/jpeg",
        });
    };

    if (!isInitialized) {
        return <PostDetailSkeleton/>;
    }

    return (
        <React.Fragment>
            {/* Inspired by https://www.youtube.com/watch?v=bU_eq8qyjic&t=5s */}
            <form
                onSubmit={handleSubmit(onSubmit)}
                onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                        // Prevent the "Enter" key from triggering form submission in single-line fields
                        const tagName = event.target.tagName.toLowerCase();
                        const isTextArea = tagName === 'textarea';


                        if (!isTextArea) {
                            event.preventDefault();
                        }
                    }
                }}
            >
                <Section
                    ref={infoSectionRef}
                    title={category.name}
                    subtitle={isRemote ? "" : "Skift kategori?"}
                    onClick={() => {navigate("/opret-annonce")}}
                    sx={{ padding: "0 20px 20px 4px" }}
                >
                    <Grid
                        container
                        spacing={2}
                        sx={{
                            width: "100%",
                            maxWidth: "830px",
                            margin: "0",
                            padding: "0 20px 20px 4px",
                            boxSizing: "border-box"
                        }}
                    >

                        <Grid item xs={12} md={6}>
                            <InputText
                                id="title"
                                defaultValue={isRemote ? remotePost.Heading : ""}
                                label="Overskrift"
                                disabled={isUploading}
                                register={register}
                                options={{
                                    required: "Påkrævet",
                                    maxLength: TITLE_MAX_LENGTH,
                                }}
                                errors={errors}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <InputTextArea
                                id="description"
                                label="Beskrivelse"
                                disabled={isUploading}
                                register={register}
                                options={{
                                    required: "Påkrævet",
                                    maxLength: BODY_MAX_LENGTH,
                                }}
                                errors={errors}
                                defaultValue={isRemote ? remotePost.Body : ""}
                            />
                        </Grid>

                        <Grid item xs={12} md={3}>
                            <DropDown
                                id="condition"
                                register={register}
                                errors={errors}
                                label="Stand"
                                disabled={isUploading}
                                items={conditions}
                                options={{
                                    required: "Påkrævet",
                                }}
                                defaultValue={isRemote ? remotePost.Condition : ""}
                                formMethods={formMethods}
                            />
                        </Grid>

                        {isInitialized
                            ? <InputZip
                                id="zipcode"
                                label="Postnr."
                                register={register}
                                disabled={isUploading}
                                options={{
                                    required: "Påkrævet",
                                    validate: (zip) => {
                                        if (zipToCity["DK"][zip] == null) return "Ugyldigt";
                                    },
                                }}
                                errors={errors}
                                defaultValue={isRemote ? remotePost.Zip : zip}
                            />
                            : <SkeletonZipField/>
                        }

                        <Grid item xs={12} md={4}>
                            <InputPrice
                                id="price"
                                label="Pris"
                                disabled={isUploading}
                                register={register}
                                options={{
                                    required: "Påkrævet",
                                }}
                                errors={errors}
                                defaultValue={isRemote ? remotePost.Price.toString() : ""}
                            />
                        </Grid>

                        <CustomFilters
                            category={category}
                            formMethods={formMethods}
                            remotePost={remotePost}
                            isUploading={isUploading}
                        />

                    </Grid>

                    {/* IMAGES */}
                    <Box sx={{padding: "0 20px 20px 20px"}}>
                        <InputFiles
                            id="images"
                            disable={images.length >= 8 || isUploading}
                            register={register}
                            maxSize={50}
                            options={{
                                validate: (_) => {
                                    if (images.length < 1) return "Påkrævet";
                                },
                            }}
                            errors={errors}
                            callback={async (files) => {
                                // Compress images
                                files = await Promise.all(
                                    Array.from(files).map(async (file) => {
                                        return await processImage(file);
                                    })
                                );

                                // Remove null - could not get .filter to work for some reason
                                const urls = [];
                                let removedFile = false;
                                files.forEach((file) => {
                                    if (file != null) urls.push(URL.createObjectURL(file));
                                    else removedFile = true;
                                });

                                // If a file was removed, notify user
                                if (removedFile) snackbar.handleOpen("Fejl med billede", "error");

                                // Push new images to existing
                                let copy = [...images];
                                copy.push(...urls);
                                copy = copy.slice(0, 8);
                                setImages(copy);
                            }}
                        />

                        {/*  Image previews */}
                        {images.length > 0 && (
                            <Box
                                className={css.row}
                                style={isMobileSize ? {display: "block"} : {}}
                            >
                                {/* Primary image column */}
                                <div>
                                    <div className={`${css.imgBox} ${css.primaryImgBox}`}>
                                        <img
                                            src={
                                                typeof images[0] === "string"
                                                    ? images[0]
                                                    : URL.createObjectURL(images[0])
                                            }
                                            className={`${css.img} ${css.primaryImg}`}
                                        />
                                    </div>
                                    <div className={css.primaryText}>
                                        <b>Primært billede.</b>
                                        <br/> Vises i søgeresultatet på Jagtbasen
                                    </div>

                                    {isMobileSize && (
                                        <Space height={"20px"}/>
                                    )}
                                </div>

                                {/* All images column */}
                                <div
                                    className={css.imgsContainer}
                                    style={isMobileSize ? {maxWidth: "20rem"} : {}}
                                >
                                    {images.map((image, index) => (
                                        <div
                                            className={`${css.imgBox} ${index == 0 && css.firstImgBox}`}
                                            key={`create-post-${image}`}
                                        >
                                            <img
                                                src={
                                                    typeof image === "string"
                                                        ? image
                                                        : URL.createObjectURL(image)
                                                }
                                                className={css.img}
                                            />

                                            {/* Image buttons like "delete" */}
                                            <div className={css.imgButtons}>
                                                {/* Hide check button on first image */}
                                                {index != 0 && (
                                                    <FaCheck
                                                        className={css.imgButton}
                                                        onClick={(_) => {
                                                            // Disable when loading
                                                            if (isUploading) return;

                                                            let copy = [...images];
                                                            const tmp = copy[0];
                                                            copy[0] = copy[index];
                                                            copy[index] = tmp;
                                                            setImages(copy);
                                                        }}
                                                        color="white"
                                                        style={{
                                                            top: "6rem",
                                                            left: "0.5rem",
                                                            backgroundColor: "green",
                                                        }}
                                                    />
                                                )}
                                                <FaTrash
                                                    className={css.imgButton}
                                                    onClick={(_) => {
                                                        // Disable when loading
                                                        if (isUploading) return;

                                                        let copy = [...images];
                                                        copy.splice(index, 1);
                                                        setImages(copy);
                                                    }}
                                                    color="white"
                                                    style={{
                                                        top: "6rem",
                                                        left: "6rem",
                                                        backgroundColor: "red",
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </Box>
                        )}
                    </Box>

                </Section>

                <Tiers tiers={category.tiers} remotePost={remotePost} formMethods={formMethods}/>

                <SizedBox height="15px"/>

                {/* Load animation */}
                {isUploading && <MobileContainer>
                    <CircularProgress size="2rem"/>
                </MobileContainer>}

                <Space height="10px"/>

                {/* Button when not loading */}
                {!isUploading && (
                    <MobileContainer>
                        <Button
                            className={css.button}
                            variant="contained"
                            color="jagtred"
                            type="submit"
                            id="submit-post"
                        >Indryk annonce</Button>
                    </MobileContainer>
                )}
            </form>
        </React.Fragment>
    );
}

export default observer(PostDetails);
